import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/shared/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, Globe, ShoppingCart, FileText, Check, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 'Tanzania',
  'Ethiopia', 'Rwanda', 'Senegal', "Côte d'Ivoire", 'Cameroon', 'Uganda',
  'Zimbabwe', 'Botswana', 'Namibia', 'Tunisia', 'Algeria', 'Angola', 'Benin',
  'United States', 'United Kingdom', 'Germany', 'France', 'China', 'India', 'UAE', 'Other'
];

const sectors = [
  'Agriculture & Food', 'Manufacturing', 'Textiles & Fashion', 'Mining & Minerals',
  'Chemicals & Pharmaceuticals', 'Electronics & Technology', 'Construction Materials',
  'Automotive & Transport', 'Energy & Renewables', 'Consumer Goods', 'Healthcare',
  'Handicrafts & Artisanal', 'Other'
];

const buyerTypes = [
  { value: 'individual', label: 'Individual Buyer' },
  { value: 'corporate', label: 'Corporate/Company' },
  { value: 'distributor', label: 'Distributor/Wholesaler' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'government', label: 'Government/Procurement Agency' },
  { value: 'other', label: 'Other' }
];

const purchasingCapacities = [
  { value: 'small', label: 'Small (< $10,000/month)' },
  { value: 'medium', label: 'Medium ($10,000 - $100,000/month)' },
  { value: 'large', label: 'Large ($100,000 - $1M/month)' },
  { value: 'enterprise', label: 'Enterprise (> $1M/month)' }
];

const paymentTerms = [
  { value: 'advance', label: '100% Advance Payment' },
  { value: 'partial', label: 'Partial Advance (30-50%)' },
  { value: 'lc', label: 'Letter of Credit (L/C)' },
  { value: 'net30', label: 'Net 30 Days' },
  { value: 'net60', label: 'Net 60 Days' },
  { value: 'escrow', label: 'Escrow Payment' }
];

const BuyerOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    organization_name: '',
    buyer_type: '',
    country: '',
    sectors_of_interest: [],
    purchasing_capacity: '',
    preferred_payment_terms: '',
    import_license_number: '',
    agree_terms: false
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSectorToggle = (sector) => {
    const current = formData.sectors_of_interest;
    if (current.includes(sector)) {
      setFormData({
        ...formData,
        sectors_of_interest: current.filter(s => s !== sector)
      });
    } else {
      setFormData({
        ...formData,
        sectors_of_interest: [...current, sector]
      });
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.organization_name && formData.buyer_type && formData.country;
      case 2:
        return formData.sectors_of_interest.length > 0;
      case 3:
        return formData.purchasing_capacity;
      case 4:
        return formData.agree_terms;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!formData.agree_terms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(`${API}/buyer/profile`, {
        organization_name: formData.organization_name,
        buyer_type: formData.buyer_type,
        country: formData.country,
        sectors_of_interest: formData.sectors_of_interest,
        purchasing_capacity: formData.purchasing_capacity,
        preferred_payment_terms: formData.preferred_payment_terms,
        import_license_number: formData.import_license_number || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Buyer profile created successfully!');
      navigate('/buyer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Organization', icon: Building2 },
    { num: 2, title: 'Interests', icon: ShoppingCart },
    { num: 3, title: 'Capacity', icon: Globe },
    { num: 4, title: 'Review', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 font-medium ${
                  step >= s.num ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  {s.title}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  step > s.num ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">
              {step === 1 && 'Organization Details'}
              {step === 2 && 'Sectors of Interest'}
              {step === 3 && 'Purchasing Information'}
              {step === 4 && 'Review & Confirm'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Tell us about your organization'}
              {step === 2 && 'Select the product categories you are interested in'}
              {step === 3 && 'Help us understand your purchasing capacity'}
              {step === 4 && 'Review your information before submitting'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Organization */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization / Company Name *</Label>
                  <Input
                    id="organization_name"
                    name="organization_name"
                    value={formData.organization_name}
                    onChange={handleChange}
                    placeholder="Enter your organization name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Buyer Type *</Label>
                  <Select value={formData.buyer_type} onValueChange={(v) => handleSelectChange('buyer_type', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select buyer type" />
                    </SelectTrigger>
                    <SelectContent>
                      {buyerTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Select value={formData.country} onValueChange={(v) => handleSelectChange('country', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Step 2: Sectors */}
            {step === 2 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {sectors.map(sector => (
                  <div
                    key={sector}
                    onClick={() => handleSectorToggle(sector)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.sectors_of_interest.includes(sector)
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        formData.sectors_of_interest.includes(sector)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {formData.sectors_of_interest.includes(sector) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium">{sector}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Capacity */}
            {step === 3 && (
              <>
                <div className="space-y-2">
                  <Label>Monthly Purchasing Capacity *</Label>
                  <Select value={formData.purchasing_capacity} onValueChange={(v) => handleSelectChange('purchasing_capacity', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select purchasing capacity" />
                    </SelectTrigger>
                    <SelectContent>
                      {purchasingCapacities.map(cap => (
                        <SelectItem key={cap.value} value={cap.value}>{cap.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Preferred Payment Terms</Label>
                  <Select value={formData.preferred_payment_terms} onValueChange={(v) => handleSelectChange('preferred_payment_terms', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select preferred payment terms" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTerms.map(term => (
                        <SelectItem key={term.value} value={term.value}>{term.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="import_license">Import License Number (Optional)</Label>
                  <Input
                    id="import_license"
                    name="import_license_number"
                    value={formData.import_license_number}
                    onChange={handleChange}
                    placeholder="Enter your import license number if applicable"
                  />
                </div>
              </>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <>
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Organization</p>
                      <p className="font-medium">{formData.organization_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Buyer Type</p>
                      <p className="font-medium">{buyerTypes.find(t => t.value === formData.buyer_type)?.label}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Country</p>
                      <p className="font-medium">{formData.country}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Purchasing Capacity</p>
                      <p className="font-medium">{purchasingCapacities.find(c => c.value === formData.purchasing_capacity)?.label}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Sectors of Interest</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.sectors_of_interest.map(sector => (
                        <span key={sector} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {sector}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="terms"
                    checked={formData.agree_terms}
                    onCheckedChange={(checked) => setFormData({ ...formData, agree_terms: checked })}
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                    I agree to the ATFFtrade.africa Terms of Service and Privacy Policy. I confirm that the information provided is accurate and I consent to verification of my buyer status.
                  </label>
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!validateStep(step)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.agree_terms}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Profile...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Complete Registration
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BuyerOnboarding;
